import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowRight, Check } from "lucide-react";
import { api } from "@/lib/api";
import { abTrack } from "@/lib/ab";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useLang } from "./FreeCards";

const KEY = "sb_free_idea_upsell_shown";
export const READ_KEY = "sb_free_idea_read";
const P = {
  en: {
    points: ["How much money you need to start", "Who your customers are", "How to find your first customers", "The first 3 steps for every idea"],
    liked: (n) => `Did you like this idea? There are ${n} more!`, same: "Every idea in Sidebizz shows you the same useful details:", once: "– pay once",
    unlock: "Unlock 150+ Ideas", later: "Maybe later", allT: (n) => `Unlock all ${n} business ideas`,
    allD: "See how each business works, how much money you need to start and how to find your first customers.",
  },
  fr: {
    points: ["Combien d’argent il faut pour commencer", "Qui sont tes clients", "Comment trouver tes premiers clients", "Les 3 premières étapes pour chaque idée"],
    liked: (n) => `Cette idée te plaît ? Il y en a ${n} autres !`, same: "Chaque idée Sidebizz te donne les mêmes infos utiles :", once: "– paiement unique",
    unlock: "Débloquer les 150+ idées", later: "Plus tard", allT: (n) => `Débloque les ${n} idées de business`,
    allD: "Découvre comment marche chaque business, combien il faut pour commencer et comment trouver tes premiers clients.",
  },
};

const ev = (type) => api.post("/free/events", { type }).catch(() => {});
const shown = (lang, where) => { ev("upsell_shown"); abTrack("upgrade_offer_viewed", { lang, props: { where } }); };
const clicked = (lang, from) => { ev("upsell_click"); abTrack("upgrade_clicked", { lang, props: { from } }); };

export function useScrollUpsell(targetId, enabled) {
  const lang = useLang();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const el = document.getElementById(targetId);
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      localStorage.setItem(READ_KEY, "1");
      ev("free_idea_read");
      if (!enabled || sessionStorage.getItem(KEY)) return;
      setTimeout(() => { sessionStorage.setItem(KEY, "1"); setOpen(true); shown(lang, "free_idea_end_popup"); }, 1200);
    }, { threshold: 0.6 });
    io.observe(el);
    return () => io.disconnect();
  }, [targetId, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  return [open, setOpen];
}

export function UpsellPopup({ open, onClose, price, more }) {
  const lang = useLang();
  const t = P[lang];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[90vw] max-w-[380px] rounded-3xl bg-white p-6 text-center gap-3" data-testid="free-upsell-popup">
        <div className="mx-auto h-14 w-14 rounded-2xl bg-[#EAF8F4] flex items-center justify-center"><Lock className="h-6 w-6 text-[#0BAF9A]" /></div>
        <DialogTitle className="font-display font-extrabold text-xl text-[#102B3F] leading-tight">{t.liked(more)}</DialogTitle>
        <DialogDescription className="text-[15px] text-[#4A5D6A]">{t.same}</DialogDescription>
        <ul className="text-left space-y-1.5 mx-auto">{t.points.map((p) => <li key={p} className="flex gap-2 text-sm text-[#102B3F]"><Check className="h-4 w-4 text-[#0BAF9A] shrink-0 mt-0.5" strokeWidth={3} />{p}</li>)}</ul>
        <div className="font-display font-extrabold text-2xl text-[#102B3F] mt-1">Rs {price} <span className="text-sm font-semibold text-[#5C6B73]">{t.once}</span></div>
        <Link to="/unlock" onClick={() => clicked(lang, "free_idea_end_popup")} className="lp-btn w-full" data-testid="free-upsell-unlock-btn">{t.unlock} <ArrowRight className="h-4 w-4" /></Link>
        <button onClick={onClose} className="text-sm font-semibold text-[#5C6B73] h-9" data-testid="free-upsell-later-btn">{t.later}</button>
      </DialogContent>
    </Dialog>
  );
}

const HALF_KEY = "sb_free_dashboard_popup_shown";

export function useHalfScrollPopup(enabled) {
  const lang = useLang();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!enabled || sessionStorage.getItem(HALF_KEY) || sessionStorage.getItem(KEY)) return;
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max <= 0 || window.scrollY / max < 0.5) return;
      window.removeEventListener("scroll", onScroll);
      sessionStorage.setItem(HALF_KEY, "1");
      setOpen(true);
      shown(lang, "dashboard_half_scroll_popup");
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  return [open, setOpen];
}

export function DashboardUnlockPopup({ open, onClose, price, total }) {
  const lang = useLang();
  const t = P[lang];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="w-[90vw] max-w-[380px] rounded-3xl bg-white p-6 text-center gap-3" data-testid="dashboard-unlock-popup">
        <div className="mx-auto h-14 w-14 rounded-2xl bg-[#EAF8F4] flex items-center justify-center"><Lock className="h-6 w-6 text-[#0BAF9A]" /></div>
        <DialogTitle className="font-display font-extrabold text-xl text-[#102B3F] leading-tight">{t.allT(total >= 150 ? "150+" : total)}</DialogTitle>
        <DialogDescription className="text-[15px] text-[#4A5D6A]">{t.allD}</DialogDescription>
        <div className="font-display font-extrabold text-2xl text-[#102B3F]" data-testid="dashboard-unlock-popup-price">Rs {price} <span className="text-sm font-semibold text-[#5C6B73]">{t.once}</span></div>
        <Link to="/unlock" onClick={() => clicked(lang, "dashboard_half_scroll_popup")} className="lp-btn w-full" data-testid="dashboard-unlock-popup-btn">{t.unlock} <ArrowRight className="h-4 w-4" /></Link>
        <button onClick={onClose} className="text-sm font-semibold text-[#5C6B73] h-9" data-testid="dashboard-unlock-popup-later-btn">{t.later}</button>
      </DialogContent>
    </Dialog>
  );
}
